"use client";

import type React from "react";
import { createContext, useContext, useEffect, useRef } from "react";
import { registerBuiltInHandlers } from "@/lib/realtime/built-in-handlers";
import { RealtimeManager } from "@/lib/realtime/realtime-manager";
import { SseTransport } from "@/lib/realtime/sse-transport";
import type { RealtimeTransport } from "@/lib/realtime/transport";
import type { RealtimeConnectionConfig } from "@/lib/realtime/types";
import { useAuth } from "./auth-provider";
interface RealtimeContextValue {
  manager: RealtimeManager;
}
const RealtimeContext = createContext<RealtimeContextValue | null>(null);
interface RealtimeProviderProps {
  children: React.ReactNode;
  config?: RealtimeConnectionConfig;
  enableBuiltInHandlers?: boolean;
  transport?: RealtimeTransport;
}
export function RealtimeProvider({
  children,
  config,
  enableBuiltInHandlers = true,
  transport = new SseTransport() // Default to SSE
}: RealtimeProviderProps) {
  const managerRef = useRef<RealtimeManager | null>(null);
  const {
    isAuthenticated,
    isLoading
  } = useAuth();

  // Initialize manager with autoConnect disabled initially
  if (!managerRef.current) {
    const modifiedConfig = {
      ...config,
      autoConnect: false // Disable auto connect, we'll handle it based on auth state
    };
    managerRef.current = RealtimeManager.getInstance(transport, modifiedConfig);
    if (enableBuiltInHandlers) {
      registerBuiltInHandlers(managerRef.current);
    }
  }

  // Handle connection based on authentication state
  useEffect(() => {
    const manager = managerRef.current;
    if (!manager || isLoading) {
      return;
    }
    if (isAuthenticated) {
      // User is authenticated, enable auto-connect and connect to realtime
      manager.setAutoConnect(true);
      manager.connect().catch(error => {
        console.error("Failed to connect to realtime:", error);
      });
    } else {
      // User is not authenticated, disable auto-connect and disconnect from realtime
      manager.setAutoConnect(false);
      manager.disconnect().catch(error_0 => {
        console.error("Failed to disconnect from realtime:", error_0);
      });
    }
  }, [isAuthenticated, isLoading]);

  // Cleanup on unmount
  useEffect(() => {
    const manager_0 = managerRef.current;
    return () => {
      manager_0?.destroy();
    };
  }, []);
  const contextValue: RealtimeContextValue = {
    manager: managerRef.current
  };
  return <RealtimeContext.Provider value={contextValue}>
			{children}
		</RealtimeContext.Provider>;
}

/**
 * Hook to get Realtime context
 */
export function useRealtimeContext() {
  const context = useContext(RealtimeContext);
  if (!context) {
    throw new Error("useRealtimeContext must be used within a RealtimeProvider");
  }
  return context;
}

/**
 * Hook to get Realtime manager from context
 */
export function useRealtimeManagerFromContext() {
  const {
    manager
  } = useRealtimeContext();
  return manager;
}